<script setup lang="ts">
import { Ref, ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { updateFolder } from './api.ts'
import { useLibraryStore } from './library.store.ts'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'

const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:externalOpen', 'folderArchived'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const analytics = getAnalyticsTracker()
const { currentFolder } = storeToRefs(libraryStore)

const folderName = computed(() => {
  return currentFolder.value.name
})

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const archiveError: Ref<boolean> = ref(false)

const closeModal = function (): void {
  visibleRef.value = false
  emit('update:externalOpen', false)
}

watch(
  () => props.externalOpen,
  () => {
    visibleRef.value = props.externalOpen
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:externalOpen', visibleRef.value)
  }
)

const archiveFolder = async function () {
  try {
    await updateFolder({
      userUuid: userStore.userId,
      folderUuid: currentFolder.value.uuid,
      updateParams: {
        archive: "true"
      }
    })
    await Promise.all([
      libraryStore.fetchFolders(userStore.userId),
      libraryStore.fetchArchivedFolders(userStore.userId)
    ])
    analytics.sendEvent('Clicked Confirm Archive Folder', {
      folder_uuid: currentFolder.value.uuid,
      folder_count: libraryStore.folderCount,
      archived_folder_count: libraryStore.archivedFolderCount
    })
    closeModal()
    archiveError.value = false
  } catch (e) {
    archiveError.value = true
    console.error(e)
  }
}
</script>
<template>
  <GbDialog
    dialogId="archive-folder-modal"
    v-model:visible="visibleRef"
    header="Archive Folder"
    :footer-buttons="true"
    @hide="closeModal"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-2 text-gray-700">
        <p class="fs-exclude">
          Are you sure you want to archive the folder "{{ folderName }}"? Any shared links will become invalid. Others will not be able to access the folder unless they have copied the folder to their library.
        </p>
        <GbInlineMessage gbTheme="red" v-if="archiveError"
          >We couldn't archive the folder. Try again later.</GbInlineMessage
        >
      </div>
    </template>
    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="closeModal"
      ></GbButton>
    </template>
    <template #footerButtonRight>
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        label="Archive"
        @click="archiveFolder"
      ></GbButton>
    </template>
  </GbDialog>
</template>
